import { type FC, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Check,
  ArrowRight,
  Loader2,
  Scale,
  ChevronRight,
  Sparkles,
} from "lucide-react";

import { NavPill } from "@/pages/layouts/components/NavPill";

import { Button } from "@/shared/components/ui/button";
import { cn, EventType, trackEvent } from "@/shared/utils/core";
import { getOnboardingFlow } from "@/shared/utils/funnelAnalytics";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { PasswordRequirement } from "@/shared/components/ui/PasswordRequirement";
import { validatePassword } from "@/shared/utils/passwordValidation";
import { useOnboardingAuth } from "@/shared/hooks/useOnboardingAuth";
import { useGoogleOAuthEnabled } from "@/shared/hooks/useGoogleOAuthEnabled";
import { startGoogleOAuth } from "@/shared/utils/googleOAuth";
import { saveLastAuthMethod } from "@/shared/utils/lastAuthMethod";
import {
  ONBOARDING_ROUTES,
  LEGAL_ROUTES,
  AUTH_ROUTES,
  LAWYER_ROUTES,
} from "@/shared/constants/paths";
import { isValidEmail } from "@/shared/utils/validators";
import googleIcon from "@/shared/assets/icons/google.svg";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { getRefPillText } from "@/shared/constants/constants";

import { getOrderedStories } from "@/pages/onboarding/authStories";
import { StoryCarousel } from "@/pages/onboarding/components/StoryCarousel";

export const CreateAccount: FC<FlowCustomComponentProps> = ({ onComplete, setAnswer, answers }) => {
  const invitationEmail = (answers["_invitation_email"] as string) || "";
  const isInviteFlow = !!invitationEmail;
  const analyticsFlow =
    isInviteFlow || !!answers["invitee-full-name"] ? "family_invite" : getOnboardingFlow(answers);

  const [email, setEmail] = useState(invitationEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const { sendVerificationCode, isLoading, error, clearError } = useOnboardingAuth({
    analyticsFlow,
  });
  const isGoogleOAuthEnabled = useGoogleOAuthEnabled();
  const [googleError, setGoogleError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const storyParam = searchParams.get("story");
  const refParam = (answers["onboarding-utm"] as string) || searchParams.get("utm_source") || "";
  const pillText = getRefPillText(refParam);
  const orderedStories = useMemo(() => getOrderedStories(storyParam), [storyParam]);

  const {
    hasMinLength,
    hasMixedCase,
    hasNumber,
    allMet: allRequirementsMet,
  } = validatePassword(password);

  const emailValid = isValidEmail(email);

  const handleSubmit = async () => {
    if (isLoading) return;

    let hasValidationError = false;

    if (!email.trim()) {
      setEmailError("Enter your email address");
      hasValidationError = true;
    } else if (!emailValid) {
      setEmailError("Enter a valid email address");
      hasValidationError = true;
    }

    if (!password) {
      setPasswordError("Enter a password");
      hasValidationError = true;
    } else if (!allRequirementsMet) {
      setPasswordError("Your password needs to meet the requirements below");
      hasValidationError = true;
    }

    if (hasValidationError) return;

    clearError();

    trackEvent(EventType.SIGNUP_SUBMITTED, { flow: analyticsFlow, signup_method: "email" });

    const verificationEnabled = answers["email-verification-enabled"] === "true";
    const success = await sendVerificationCode({ email, password }, undefined, {
      skipVerification: !verificationEnabled,
    });

    if (success) {
      saveLastAuthMethod("password");
      setAnswer("signup-email", email);
      onComplete();
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleError(null);
    trackEvent(EventType.SIGNUP_SUBMITTED, { flow: analyticsFlow, signup_method: "google" });

    const isInvite = !!answers["invitee-full-name"];

    if (isInvite) {
      const token = (answers["_invite_token"] as string) ?? "";
      const oauthError = await startGoogleOAuth({
        type: "invite-new",
        redirectPath: `${window.location.pathname}${window.location.search}`,
        answers,
        token,
      });
      if (oauthError) setGoogleError(oauthError);
    } else {
      const oauthError = await startGoogleOAuth({
        type: "onboarding",
        redirectPath: ONBOARDING_ROUTES.ACCOUNT,
        answers,
      });
      if (oauthError) setGoogleError(oauthError);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const displayError = googleError ?? error;

  return (
    <div className="fixed inset-0 z-50 flex h-screen bg-subtle">
      <div className="flex flex-1 flex-col overflow-y-auto px-8 py-8">
        <img src={logoFull} alt="Elayne" className="h-7 self-start" />

        <div className="flex flex-1 items-center justify-center">
          <div className="flex w-full max-w-md flex-col gap-5">
            <div className="flex flex-col items-center gap-3">
              <NavPill icon={Sparkles} label={pillText} variant="highlight" />
              <h2 className="text-center">Create your free account</h2>
            </div>

            {isGoogleOAuthEnabled && (
              <>
                <Button
                  variant="outline"
                  rounded={true}
                  className="w-full gap-2"
                  onClick={handleGoogleSignUp}
                >
                  <img src={googleIcon} alt="Google" className="h-4 w-4" />
                  Continue with Google
                </Button>

                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-stone-200" />
                  <span className="text-sm text-secondary">OR</span>
                  <div className="h-px flex-1 bg-stone-200" />
                </div>
              </>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="signup-email" className="text-sm font-medium text-primary">
                Email address
              </label>
              <Input
                id="signup-email"
                type="email"
                placeholder="example@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                onKeyDown={handleKeyDown}
                disabled={isInviteFlow}
                className={cn(isInviteFlow && "bg-subtle", emailError && "border-destructive")}
              />
              {emailError && <FieldError message={emailError} />}
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="signup-password" className="text-sm font-medium text-primary">
                Password
              </label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(null);
                  }}
                  onFocus={() => setPasswordFocused(true)}
                  onKeyDown={handleKeyDown}
                  className={`pr-10 ${passwordError ? "border-destructive" : ""}`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordError && <FieldError message={passwordError} />}
            </div>

            {(passwordFocused || password.length > 0) && (
              <div className="flex flex-col gap-2">
                {allRequirementsMet ? (
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" strokeWidth={3} />
                    <span className="text-sm text-primary">All requirements met</span>
                  </div>
                ) : (
                  <>
                    <PasswordRequirement met={hasMinLength} text="At least 8 characters" />
                    <PasswordRequirement met={hasMixedCase} text="Upper and lowercase letters" />
                    <PasswordRequirement met={hasNumber} text="At least 1 number" />
                  </>
                )}
              </div>
            )}

            {displayError && <FieldError message={displayError} />}

            <Button
              variant="brand"
              rounded={true}
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Create account <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            <p className="text-center text-2xs text-secondary">
              By creating an account, you agree to our{" "}
              <Link
                to={LEGAL_ROUTES.TERMS_OF_SERVICE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary underline"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to={LEGAL_ROUTES.PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary underline"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <p className="text-center text-sm text-secondary">
              Already have an account?{" "}
              <Link to={AUTH_ROUTES.SIGN_IN} className="text-brand underline">
                Log in
              </Link>
            </p>
          </div>
        </div>

        <div className="flex justify-center pt-4">
          <Link
            to={LAWYER_ROUTES.SIGNUP}
            className="inline-flex items-center gap-2 rounded-full border border-primary bg-white px-4 py-2 text-sm no-underline hover:bg-subtle"
          >
            <Scale className="h-4 w-4 shrink-0 text-brand" />
            <span className="text-primary whitespace-nowrap">Attorney?</span>
            <span className="text-primary underline whitespace-nowrap">
              Access the partner portal
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-secondary" />
          </Link>
        </div>
      </div>

      <div className="hidden p-5 pl-0 lg:flex lg:w-1/3">
        <StoryCarousel stories={orderedStories} className="flex-1" />
      </div>
    </div>
  );
};
